import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { PlanFrequencyTable, PlanLimitTable } from "../components/legal/PlanTable";
import { useAccountState } from "../hooks/useAccountState";
import { api, type BillingSubscriptionData, type PlanType } from "../lib/api";
import { canUpgradeToPaid, isPaidPlan } from "../lib/billing";
import { logError, toUserMessage } from "../lib/errors";
import { PLAN_PRICES, PLAN_UNLIMITED_NOTE } from "../lib/planCatalog";

type PlansLocationState = {
  from?: unknown;
  returnTo?: unknown;
};

const AUTH_ENTRY_PATHS = new Set(["/", "/login", "/splash", "/register", "/start"]);
const PROTECTED_ENTRY_PREFIXES = [
  "/home",
  "/talk",
  "/request",
  "/chat",
  "/profile",
  "/character-switch",
  "/receipt",
  "/executive-memos",
];

function normalizeReturnPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

function isProtectedEntryPath(path: string): boolean {
  return PROTECTED_ENTRY_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function resolvePlansReturnPath(state: PlansLocationState | null, isAuthenticated: boolean): string {
  const explicitPath = normalizeReturnPath(state?.returnTo) ?? normalizeReturnPath(state?.from);

  if (explicitPath) {
    if (isAuthenticated && AUTH_ENTRY_PATHS.has(explicitPath)) {
      return "/home";
    }
    if (!isAuthenticated && isProtectedEntryPath(explicitPath)) {
      return "/";
    }
    return explicitPath;
  }

  return isAuthenticated ? "/home" : "/";
}

function getPlanLabel(plan: PlanType | null | undefined): string | null {
  if (plan === "paid") return "スタンダードプラン";
  if (plan === "executive") return "エグゼクティブプラン";
  if (plan === "free") return "フリープラン";
  if (plan === "invite_trial") return "特典コード";
  return null;
}

export default function PlansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountState, loading, error } = useAccountState();
  const [standardLoading, setStandardLoading] = React.useState(false);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [billingSubscription, setBillingSubscription] = React.useState<BillingSubscriptionData | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const billingAvailable = accountState?.authStatus === "authenticated" && (accountState.billingEnabled ?? false);
  const canStartNewCheckout = billingAvailable && canUpgradeToPaid(accountState);
  const canOpenPortal = billingAvailable && isPaidPlan(accountState);
  const actionBusy = standardLoading || portalLoading;
  const isAuthenticated = accountState?.authStatus === "authenticated";
  const returnPath = React.useMemo(
    () => resolvePlansReturnPath((location.state as PlansLocationState | null) ?? null, isAuthenticated),
    [isAuthenticated, location.state]
  );
  const currentPlanLabel = getPlanLabel(accountState?.plan);
  const currentPlanKey = accountState?.plan === "paid" ? "standard" : accountState?.plan === "executive" ? "executive" : accountState?.plan === "free" ? "free" : null;
  const isSubscriptionCanceled = canOpenPortal && billingSubscription?.cancelAtPeriodEnd === true;
  const canOpenPlanManagement = canOpenPortal || canStartNewCheckout;

  React.useEffect(() => {
    if (!canOpenPortal) {
      setBillingSubscription(null);
      return;
    }

    let disposed = false;

    api.getBillingSubscription()
      .then((subscription) => {
        if (!disposed) setBillingSubscription(subscription);
      })
      .catch((errorValue) => {
        if (disposed) return;
        logError(errorValue);
        setBillingSubscription(null);
      });

    return () => {
      disposed = true;
    };
  }, [canOpenPortal]);

  const openBillingPortal = React.useCallback(async () => {
    if (!canOpenPortal || portalLoading) return;
    setPortalLoading(true);
    setActionError(null);
    try {
      const session = await api.createBillingPortalSession();
      window.location.href = session.url;
    } catch (errorValue) {
      logError(errorValue);
      setActionError(toUserMessage(errorValue));
    } finally {
      setPortalLoading(false);
    }
  }, [canOpenPortal, portalLoading]);

  const startStandardCheckout = React.useCallback(async () => {
    if (!canStartNewCheckout || standardLoading) return;
    setStandardLoading(true);
    setActionError(null);
    try {
      const session = await api.createBillingCheckoutSession("standard_monthly");
      window.location.href = session.url;
    } catch (errorValue) {
      logError(errorValue);
      setActionError(toUserMessage(errorValue));
    } finally {
      setStandardLoading(false);
    }
  }, [canStartNewCheckout, standardLoading]);

  const openPlanManagement = React.useCallback(() => {
    if (canOpenPortal) {
      void openBillingPortal();
      return;
    }
    void startStandardCheckout();
  }, [canOpenPortal, openBillingPortal, startStandardCheckout]);

  const planManagementActionLabel = actionBusy ? "移動中..." : "契約確認・プラン変更へ";
  const planManagementActionDisabled = loading || actionBusy || !canOpenPlanManagement;

  return (
    <main className="min-h-screen bg-[#FDF8F5] px-6 py-10 text-[#4A3E39] antialiased">
      <article className="mx-auto max-w-5xl rounded-[2rem] border border-[#EADBC8] bg-white px-6 py-8 shadow-[0_14px_28px_rgba(166,124,82,0.10)] md:px-10">
        <header className="mb-8 flex items-center justify-between gap-4">
          <button type="button" onClick={() => navigate(returnPath)} className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </button>
          <Link to={isAuthenticated ? "/home" : "/"} className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <div className="space-y-8 text-sm leading-7 text-[#5D4D46]">
          <section className="space-y-3">
            <h1 className="text-2xl font-bold text-[#5D4D46]">料金プラン</h1>
            <p>
              {isAuthenticated
                ? "プラン内容、料金、主要な利用上限を確認できます。"
                : "WizMateの料金プランと主な利用上限を確認できます。"}
            </p>
            {loading ? <p className="text-xs font-bold text-[#9C867D]">アカウント状態を確認中です。</p> : null}
            {error ? <p className="text-xs font-bold text-[#A46D4C]">ログイン状態を確認できませんでした。ログイン後にプランを選択してください。</p> : null}
            {!loading && accountState?.authStatus === "authenticated" && !billingAvailable ? (
              <p className="text-xs font-bold text-[#A46D4C]">現在、このアカウントではプラン変更機能を利用できません。</p>
            ) : null}
            {actionError ? <p role="alert" className="text-xs font-bold text-[#A46D4C]">{actionError}</p> : null}
            {isAuthenticated ? (
              <div className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F1] px-4 py-4 text-xs leading-6 text-[#6B5851]">
                {isAuthenticated && currentPlanLabel ? (
                  <p className="font-black text-[#5D4D46]">現在のプラン：{currentPlanLabel}</p>
                ) : null}
                {isSubscriptionCanceled && currentPlanLabel ? (
                  <>
                    <p className="mt-2 font-bold text-[#5D4D46]">{currentPlanLabel}は解約済みです。</p>
                    <p>契約内容の確認、プラン変更、解約は次の決済管理画面で行えます。</p>
                    <p>プランを変更する場合は「サブスクリプションを更新」、</p>
                    <p>解約する場合は「サブスクリプションをキャンセル」を選択してください。</p>
                    <p>有効期限までは引き続き利用できます。</p>
                    <p>次回以降の請求は行われません。</p>
                    <p>日割り返金はありません。</p>
                  </>
                ) : (
                  <>
                    <p className={isAuthenticated && currentPlanLabel ? "mt-2" : ""}>
                      {canStartNewCheckout
                        ? "有料プランのお申し込み、契約内容の確認、プラン変更、解約は次の決済管理画面で行えます。"
                        : "契約内容の確認、プラン変更、解約は次の決済管理画面で行えます。"}
                    </p>
                    <p>プランを変更する場合は「サブスクリプションを更新」、</p>
                    <p>解約する場合は「サブスクリプションをキャンセル」を選択してください。</p>
                    <p>解約した場合も、すでにお支払い済みの期間中は引き続き利用できます。</p>
                    <p>日割り返金はありません。</p>
                  </>
                )}
                {canOpenPlanManagement ? (
                  <button
                    type="button"
                    onClick={openPlanManagement}
                    disabled={planManagementActionDisabled}
                    className="mt-4 rounded-2xl bg-[#7C5A4E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#6D4D42] disabled:cursor-not-allowed disabled:bg-[#D8C7BA]"
                  >
                    {planManagementActionLabel}
                  </button>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="grid gap-4 md:grid-cols-3">
            {PLAN_PRICES.map((plan) => {
              const isCurrentPlan = currentPlanKey === plan.key;

              return (
                <article key={plan.key} className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-5 py-5">
                  {isCurrentPlan ? (
                    <span className="mb-3 inline-flex rounded-full bg-[#F4E6DA] px-3 py-1 text-[11px] font-black text-[#7C5A4E]">
                      現在利用中
                    </span>
                  ) : null}
                  <p className="text-base font-black text-[#5D4D46]">{plan.name}</p>
                  <p className="mt-2 text-lg font-black text-[#7C5A4E]">{plan.priceLabel}</p>
                  <p className="mt-2 text-xs leading-6 text-[#7B6861]">{plan.description}</p>

                  {plan.key === "free" ? (
                    <p className="mt-5 rounded-2xl bg-[#FDF8F5] px-4 py-3 text-xs font-bold text-[#7B6861]">利用登録後、無料で開始できます。</p>
                  ) : null}
                </article>
              );
            })}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-[#5D4D46]">主要な利用上限</h2>
            <PlanLimitTable />
            <p>※ {PLAN_UNLIMITED_NOTE}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-[#5D4D46]">定期配信頻度</h2>
            <PlanFrequencyTable />
            <p>スタンダードプランのレポート定期配信は3枠ですが、毎日は対象外です。エグゼクティブプランでは日次レポートに対応します。</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-[#5D4D46]">確認事項</h2>
            <p>特典コードをお持ちの方は、フリープランの上限枠を一定期間拡大できます。メニューの「特典コードを入力」から適用してください。</p>
            <div className="flex flex-wrap gap-3 text-xs font-bold">
              <Link to="/terms" className="underline underline-offset-4">
                利用規約
              </Link>
              <Link to="/privacy" className="underline underline-offset-4">
                プライバシーポリシー
              </Link>
              <Link to="/commercial-transactions" className="underline underline-offset-4">
                特定商取引法に基づく表記
              </Link>
            </div>
          </section>
        </div>
      </article>
    </main>
  );
}
